// PhotoViewer — a minimal lightbox for viewing committed report photos
// in their original (full-resolution) form.
//
// Design goals:
//   - Fetch the original image via the authenticated Worker proxy
//     (`GET /reports/:id/photos/:photoId/image`) so no R2 credentials
//     are needed and no bucket becomes public.
//   - No third-party dependencies; uses a native <dialog> element
//     for backdrop management and focus trapping.
//   - Proper blob URL lifecycle: fetch on open, revoke on close.
//   - Keyboard dismiss (Escape) without needing a ref.
//   - Graceful loading and error states.
//   - Does not change the current URL or navigation state.
//
// Interaction features:
//   - Mouse wheel zoom with zoom-to-cursor (min 1x, max 4x).
//   - Left-button pan when image is zoomed (scale > 1).
//   - Boundary constraints prevent dragging image completely out of view.
//   - Visual cursor feedback (grab / grabbing).

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReportPhotoJson } from '@/api/types';
import { photosApi } from '@/api/reportsApi';

export interface PhotoViewerProps {
  /** The committed photo to display. Must have an r2_key (the original object key). */
  photo: ReportPhotoJson;
  /** Bearer-token provider. */
  getToken: () => string | null;
  /** Called when the viewer should close. The parent owns the open/close state. */
  onClose: () => void;
}

/** Blob URL lifecycle states. */
type ViewerState = 'loading' | 'ready' | 'error';

/** Zoom scale limits */
const MIN_SCALE = 1;
const MAX_SCALE = 4;
/** Zoom step per wheel delta unit */
const ZOOM_SENSITIVITY = 0.002;

export function PhotoViewer({ photo, getToken, onClose }: PhotoViewerProps) {
  const [state, setState] = useState<ViewerState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  /** The current blob URL. Null when loading or on error. */
  const [src, setSrc] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Image transform state
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Refs for drag calculations - use refs to avoid stale closures
  const dragStartRef = useRef({ x: 0, y: 0 });
  const translateStartRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const scaleRef = useRef(1);
  const translateXRef = useRef(0);
  const translateYRef = useRef(0);

  // Keep refs in sync with state
  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    translateXRef.current = translateX;
  }, [translateX]);

  useEffect(() => {
    translateYRef.current = translateY;
  }, [translateY]);

  // Reset transform when a new photo loads
  const resetTransform = useCallback(() => {
    setScale(1);
    setTranslateX(0);
    setTranslateY(0);
    setIsDragging(false);
    isDraggingRef.current = false;
  }, []);

  // Fetch the original image when the component mounts.
  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;

    async function load() {
      try {
        const path = photosApi.imageUrl(photo.report_id, photo.id);
        blobUrl = await photosApi.fetchAsset(getToken, path);
        if (cancelled) {
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          return;
        }
        setSrc(blobUrl);
        setState('ready');
        // Reset transform for new photo
        resetTransform();
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Could not load the image.';
        setErrorMsg(msg);
        setState('error');
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [photo, getToken, resetTransform]);

  // Show the dialog when the component mounts (after the first render).
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) {
      dialog.showModal();
    }
    return () => {
      // Clean up: revoke any blob URL and close the dialog.
      if (src) URL.revokeObjectURL(src);
      if (dialog.open) dialog.close();
    };
  }, [src]);

  // Keyboard: Escape closes the viewer.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    // Only close when clicking the backdrop (the <dialog> element itself),
    // not when clicking the inner content.
    if (e.target === dialogRef.current) onClose();
  };

  // ── Wheel zoom handler ───────────────────────────────────────────────────────
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const container = imageContainerRef.current;
      if (!container) return;

      // Get cursor position relative to the container
      const rect = container.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;

      // Calculate zoom using refs to avoid stale closures
      const delta = -e.deltaY * ZOOM_SENSITIVITY;
      const currentScale = scaleRef.current;
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, currentScale * (1 + delta)));

      if (newScale === currentScale) {
        // Still need to update translation even if scale didn't change (e.g., at boundaries)
        const imgEl = imgRef.current;
        if (!imgEl) return;

        const containerWidth = rect.width;
        const containerHeight = rect.height;
        const currentTranslateX = translateXRef.current;
        const currentTranslateY = translateYRef.current;
        const scaledWidth = imgEl.naturalWidth * newScale;
        const scaledHeight = imgEl.naturalHeight * newScale;

        const maxTranslateX = Math.max(0, (scaledWidth - containerWidth) / 2);
        const maxTranslateY = Math.max(0, (scaledHeight - containerHeight) / 2);

        setTranslateX(Math.min(maxTranslateX, Math.max(-maxTranslateX, currentTranslateX)));
        setTranslateY(Math.min(maxTranslateY, Math.max(-maxTranslateY, currentTranslateY)));
        return;
      }

      // Use refs to get current translate values (avoid stale closures)
      const currentTranslateX = translateXRef.current;
      const currentTranslateY = translateYRef.current;

      // Calculate new translation with zoom-to-cursor
      const scaleRatio = newScale / currentScale;
      const newTranslateX = cursorX - scaleRatio * (cursorX - currentTranslateX);
      const newTranslateY = cursorY - scaleRatio * (cursorY - currentTranslateY);

      // Apply boundary constraints after zoom
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const imgEl = imgRef.current;
      if (imgEl) {
        const scaledWidth = imgEl.naturalWidth * newScale;
        const scaledHeight = imgEl.naturalHeight * newScale;

        // Clamp translation so image doesn't go completely out of bounds
        const maxTranslateX = Math.max(0, (scaledWidth - containerWidth) / 2);
        const maxTranslateY = Math.max(0, (scaledHeight - containerHeight) / 2);

        const clampedTranslateX = Math.min(maxTranslateX, Math.max(-maxTranslateX, newTranslateX));
        const clampedTranslateY = Math.min(maxTranslateY, Math.max(-maxTranslateY, newTranslateY));

        // Update state: set both scale and translate atomically
        setTranslateX(clampedTranslateX);
        setTranslateY(clampedTranslateY);
        setScale(newScale);
      } else {
        setTranslateX(newTranslateX);
        setTranslateY(newTranslateY);
        setScale(newScale);
      }
    },
    [], // No dependencies - uses refs
  );

  // Attach wheel event listener to container
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── Pointer drag handlers ───────────────────────────────────────────────────
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only respond to left mouse button (primary)
      if (e.button !== 0) return;

      // Only allow dragging when zoomed in
      if (scaleRef.current <= 1) return;

      e.preventDefault();
      e.stopPropagation();

      const container = imageContainerRef.current;
      if (container) {
        try {
          container.setPointerCapture(e.pointerId);
        } catch {
          // setPointerCapture may not be available in all environments (e.g., JSDOM)
        }
      }

      dragStartRef.current = { x: e.clientX, y: e.clientY };
      // Use refs to get current translate values
      translateStartRef.current = { x: translateXRef.current, y: translateYRef.current };
      isDraggingRef.current = true;
      setIsDragging(true);
    },
    [], // No dependencies - uses refs
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current) return;

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    const newTranslateX = translateStartRef.current.x + deltaX;
    const newTranslateY = translateStartRef.current.y + deltaY;

    // Apply boundary constraints
    const container = imageContainerRef.current;
    const imgEl = imgRef.current;
    if (container && imgEl) {
      const rect = container.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;
      const currentScale = scaleRef.current;
      const scaledWidth = imgEl.naturalWidth * currentScale;
      const scaledHeight = imgEl.naturalHeight * currentScale;

      const maxTranslateX = Math.max(0, (scaledWidth - containerWidth) / 2);
      const maxTranslateY = Math.max(0, (scaledHeight - containerHeight) / 2);

      setTranslateX(Math.min(maxTranslateX, Math.max(-maxTranslateX, newTranslateX)));
      setTranslateY(Math.min(maxTranslateY, Math.max(-maxTranslateY, newTranslateY)));
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    const container = imageContainerRef.current;
    if (container) {
      try {
        if (container.hasPointerCapture(e.pointerId)) {
          container.releasePointerCapture(e.pointerId);
        }
      } catch {
        // Ignore errors in environments without pointer capture support
      }
    }
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  const handlePointerCancel = useCallback((e: React.PointerEvent) => {
    const container = imageContainerRef.current;
    if (container) {
      try {
        if (container.hasPointerCapture(e.pointerId)) {
          container.releasePointerCapture(e.pointerId);
        }
      } catch {
        // Ignore errors in environments without pointer capture support
      }
    }
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  // Handle image load to get natural dimensions
  const handleImageLoad = useCallback(() => {
    // Reset transform when new image loads
    resetTransform();
  }, [resetTransform]);

  // Compute cursor style
  const cursorStyle = scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default';

  return (
    <dialog
      ref={dialogRef}
      className="photo-viewer"
      onClick={handleBackdropClick}
      aria-label="Photo viewer"
    >
      <div className="photo-viewer__content">
        {/* Header bar */}
        <div className="photo-viewer__header">
          <span className="photo-viewer__label">Original photo</span>
          <button
            type="button"
            className="photo-viewer__close"
            onClick={onClose}
            aria-label="Close photo viewer"
          >
            ✕
          </button>
        </div>

        {/* Image area */}
        <div
          ref={imageContainerRef}
          className="photo-viewer__body"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          style={{ cursor: cursorStyle }}
        >
          {state === 'loading' && (
            <div className="photo-viewer__loading" aria-live="polite">
              Loading original…
            </div>
          )}
          {state === 'error' && (
            <div className="photo-viewer__error" role="alert">
              <p>{errorMsg}</p>
              <button type="button" className="btn" onClick={onClose}>
                Close
              </button>
            </div>
          )}
          {state === 'ready' && src && (
            <img
              ref={imgRef}
              src={src}
              alt="Original report photo"
              className="photo-viewer__img"
              draggable={false}
              onLoad={handleImageLoad}
              style={{
                transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
                transformOrigin: 'center center',
              }}
            />
          )}
        </div>
      </div>
    </dialog>
  );
}
